import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebaseConfig';
import type { RegionalFeed } from '../utils/liveNewsRegions';
import { countryNewsFeeds, regionalNewsFeeds } from '../utils/liveNewsRegions';
import { embedVideoId, loadYouTubePlayerApi } from '../utils/youtubePlayer';

interface FeedCardProps {
    feed: RegionalFeed;
    onEnlarge: (url: string, label: string) => void;
    onUnavailable?: (label: string) => void;
}

/** Individual feed card with multi-URL fallback and graceful offline state */
const FeedCard: React.FC<FeedCardProps> = ({ feed, onEnlarge, onUnavailable }) => {
    const [urlIndex, setUrlIndex] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const [isOffline, setIsOffline] = useState(false);
    const playerContainer = useRef<HTMLDivElement | null>(null);
    const sourceUrl = new URL(feed.urls[urlIndex]);
    if (sourceUrl.hostname === 'player.vimeo.com') {
        sourceUrl.searchParams.set('autoplay', '1');
        sourceUrl.searchParams.set('muted', '1');
    }
    sourceUrl.searchParams.set('_k', String(refreshKey));
    const currentUrl = sourceUrl.toString();
    const videoId = embedVideoId(currentUrl);

    const tryNextSource = useCallback(() => {
        const nextIdx = urlIndex + 1;
        if (nextIdx < feed.urls.length) {
            setUrlIndex(nextIdx);
        } else {
            setIsOffline(true);
            onUnavailable?.(feed.label);
        }
    }, [urlIndex, feed.urls.length, feed.label, onUnavailable]);

    useEffect(() => {
        if (!videoId || isOffline)
            return;
        let active = true;
        let player: { destroy(): void } | null = null;
        void loadYouTubePlayerApi().then(api => {
            if (!active || !playerContainer.current)
                return;
            const target = document.createElement('div');
            playerContainer.current.replaceChildren(target);
            player = new api.Player(target, {
                videoId, width: '100%', height: '100%',
                playerVars: { autoplay: 1, mute: 1, controls: 1, playsinline: 1, origin: window.location.origin },
                events: { onError: () => { if (active) tryNextSource(); } }
            });
        }).catch(() => { if (active) tryNextSource(); });
        return () => {
            active = false;
            player?.destroy();
            playerContainer.current?.replaceChildren();
        };
    }, [videoId, refreshKey, isOffline, tryNextSource]);

    const handleRefresh = () => {
        setUrlIndex(0);
        setIsOffline(false);
        setRefreshKey(k => k + 1);
    };

    if (isOffline) {
        return (
            <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-slate-800 bg-slate-950 flex flex-col items-center justify-center gap-2 group/feed">
                <div className="text-slate-600 text-2xl">📡</div>
                <p className="text-slate-500 text-[9px] font-bold uppercase tracking-widest text-center px-2">
                    {feed.label}
                </p>
                <p className="text-slate-700 text-[8px] text-center px-2">Stream Temporarily Offline</p>
                <button
                    onClick={handleRefresh}
                    className="mt-1 px-3 py-1 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-400 hover:text-white rounded-lg text-[9px] font-bold uppercase tracking-wider transition-all"
                >
                    Retry
                </button>
                {/* Region badge */}
                <div className="absolute top-2 left-2 bg-slate-900/80 px-2 py-0.5 rounded text-[8px] text-slate-500 font-bold uppercase tracking-wider">
                    {feed.region}
                </div>
            </div>
        );
    }

    return (
        <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-slate-800 shadow-2xl bg-slate-950 group/feed">
            {/* Loading shimmer */}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 z-0 pointer-events-none">
                <div className="w-5 h-5 border-2 border-slate-700 border-t-red-500 rounded-full animate-spin" />
                <span className="text-slate-600 text-[8px] font-bold uppercase tracking-widest">Connecting...</span>
            </div>

            {videoId ? <div key={`${feed.label}-${urlIndex}-${refreshKey}`} ref={playerContainer} className="relative z-10 w-full h-full" /> : (
                <iframe key={`${feed.label}-${urlIndex}-${refreshKey}`} src={currentUrl}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen referrerPolicy="no-referrer-when-downgrade"
                    className="relative z-10 w-full h-full" title={`${feed.label} Live`} onError={tryNextSource} />
            )}

            {/* Bottom gradient + label */}
            <div className="absolute inset-x-0 bottom-0 top-[55%] bg-gradient-to-t from-slate-950 via-slate-900/50 to-transparent pointer-events-none z-20" />
            <div className="absolute bottom-2 left-2 right-2 pointer-events-none z-20">
                <h4 className="text-white font-bold text-[10px] md:text-xs mb-0 drop-shadow-lg leading-tight truncate">{feed.label}</h4>
                <p className="hidden md:block text-slate-500 text-[7px] uppercase tracking-wider font-semibold">{feed.region}</p>
            </div>

            {/* Live dot */}
            <div className="absolute top-2 left-2 z-20 flex items-center gap-1.5 pointer-events-none">
                <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                </span>
                <span className="hidden xl:inline text-white text-[8px] font-bold tracking-widest uppercase drop-shadow-md">Live</span>
            </div>

            {/* Source indicator (when on fallback URL) */}
            {urlIndex > 0 && (
                <div className="absolute top-2 right-2 z-20 bg-amber-500/20 border border-amber-500/30 text-amber-400 text-[7px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider pointer-events-none">
                    Alt Source
                </div>
            )}

            {/* Hover controls */}
            <div className="absolute inset-0 z-30 opacity-0 group-hover/feed:opacity-100 transition-opacity bg-black/60 flex flex-col items-center justify-center gap-1.5 p-2">
                <button
                    onClick={() => onEnlarge(currentUrl, feed.label)}
                    className="pointer-events-auto bg-blue-600 hover:bg-blue-500 text-white w-full py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all border border-blue-400"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                    </svg>
                    Fullscreen
                </button>
                {feed.urls.length > 1 && (
                    <button
                        onClick={tryNextSource}
                        className="pointer-events-auto bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white w-full py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all border border-slate-600"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3 h-3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>
                        Switch Source
                    </button>
                )}
                <button
                    onClick={handleRefresh}
                    className="pointer-events-auto bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white w-full py-1.5 rounded-lg font-bold text-[10px] uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all border border-slate-700"
                >
                    Refresh
                </button>
            </div>
        </div>
    );
};

interface GlobalLiveNewsGridProps {
    maxFeeds?: number;
    layout?: 'grid' | 'horizontal';
    coverageCountries?: string[];
    lockedCountry?: string;
}

export const GlobalLiveNewsGrid: React.FC<GlobalLiveNewsGridProps> = ({ maxFeeds, layout = 'grid', coverageCountries = [], lockedCountry }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
    const [expandedVideo, setExpandedVideo] = useState<{ url: string; label: string } | null>(null);
    const [resolvedFeeds, setResolvedFeeds] = useState<RegionalFeed[] | null>(null);
    const [unavailableLabels, setUnavailableLabels] = useState<Set<string>>(new Set());
    const [directoryStatus, setDirectoryStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading');
    const [reloadKey, setReloadKey] = useState(0);
    const markUnavailable = useCallback((label: string) =>
        setUnavailableLabels(previous => new Set(previous).add(label)), []);

    useEffect(() => {
        let active = true;
        const load = async () => {
            try {
                const getSources = httpsCallable<unknown, { status: string; feeds: RegionalFeed[] }>(
                    functions, 'getLiveNewsSources', { timeout: 120000 }
                );
                for (let attempt = 0; attempt < 3; attempt++) {
                    const { data } = await getSources({});
                    if (!active)
                        return;
                    if (data.status === 'refreshing') {
                        await new Promise(resolve => window.setTimeout(resolve, 8000));
                        continue;
                    }
                    if (data.status !== 'ready')
                        break;
                    setResolvedFeeds((Array.isArray(data.feeds) ? data.feeds : []).filter(feed =>
                        typeof feed.label === 'string' && typeof feed.region === 'string' &&
                        Array.isArray(feed.urls) && feed.urls.length > 0 &&
                        feed.urls.every(url => typeof url === 'string' && url.startsWith('https://'))));
                    setUnavailableLabels(new Set());
                    setDirectoryStatus('ready');
                    return;
                }
            } catch {
                // A failed directory request never falls back to stale video IDs.
            }
            if (active)
                setDirectoryStatus('unavailable');
        };
        setDirectoryStatus('loading');
        void load();
        return () => { active = false; };
    }, [reloadKey]);

    useEffect(() => {
        if (!expandedVideo)
            return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape')
                setExpandedVideo(null);
        };
        document.addEventListener('keydown', handleKeyDown);
        const stopWhenHidden = () => { if (document.hidden) setExpandedVideo(null); };
        document.addEventListener('visibilitychange', stopWhenHidden);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('visibilitychange', stopWhenHidden);
        };
    }, [expandedVideo]);

    const availableFeeds = (resolvedFeeds || []).filter(feed => !unavailableLabels.has(feed.label));
    const countryFilter = lockedCountry || selectedFilter || (directoryStatus !== 'ready' ? '' :
        coverageCountries.length > 0 && regionalNewsFeeds(availableFeeds, coverageCountries).length === 0
            ? 'global' : 'locations');
    const allFeeds = availableFeeds;
    const scopedFeeds = lockedCountry ? countryNewsFeeds(availableFeeds, lockedCountry, searchTerm ? undefined : maxFeeds)
        : countryFilter === 'all'
        ? allFeeds
        : countryFilter === 'global'
            ? availableFeeds.filter(feed => feed.global)
            : regionalNewsFeeds(
                availableFeeds,
                countryFilter === 'locations' ? coverageCountries : [countryFilter],
                searchTerm ? undefined : maxFeeds
            );
    const filteredFeeds = (searchTerm && !lockedCountry ? allFeeds : scopedFeeds).filter(f =>
        f.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.region.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const displayFeeds = maxFeeds && !searchTerm && countryFilter !== 'all' ? filteredFeeds.slice(0, maxFeeds) : filteredFeeds;
    const gridCols = layout === 'horizontal'
        ? 'grid-cols-2 md:grid-cols-4 lg:grid-cols-8'
        : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';

    return (
        <div className="space-y-4">
            {!lockedCountry && coverageCountries.length > 0 && (
                <div className="flex flex-wrap gap-2" aria-label="Live news locations">
                    {[
                        { value: 'locations', label: 'My locations' },
                        ...coverageCountries.map(country => ({ value: country, label: country })),
                        { value: 'global', label: 'Global' },
                        { value: 'all', label: 'All feeds' }
                    ].map(option => (
                        <button key={option.value} type="button" aria-pressed={countryFilter === option.value}
                            onClick={() => { setSelectedFilter(option.value); setSearchTerm(''); }}
                            className={`rounded-full border px-3 py-1 text-xs font-semibold ${countryFilter === option.value ? 'border-blue-400 bg-blue-600 text-white' : 'border-slate-700 text-slate-400 hover:text-white'}`}>
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
            {!lockedCountry && coverageCountries.length > 0 && countryFilter !== 'all' && !searchTerm &&
                !resolvedFeeds?.some(feed => !feed.global && feed.countries?.includes(countryFilter)) && !['locations', 'global'].includes(countryFilter) && directoryStatus === 'ready' && (
                    <p className="text-xs text-slate-400">No dedicated {countryFilter} stream yet. Showing regional coverage.</p>
            )}
            {/* Search feeds */}
            <div className="relative group/search">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-slate-500 group-hover/search:text-blue-400 transition-colors">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                </div>
                <input
                    type="text"
                    placeholder="Search feeds by name or region..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl py-3 pl-11 pr-12 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all shadow-inner"
                />
                <div className="absolute inset-y-0 right-2 flex items-center gap-2">
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="text-slate-500 hover:text-white p-2 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Feed Grid */}
            {directoryStatus === 'loading' && <p className="text-xs text-slate-400">Checking live channels...</p>}
            {directoryStatus === 'unavailable' && (
                <div className="flex items-center gap-3 text-xs text-slate-400">
                    <span>Live channel directory is temporarily unavailable.</span>
                    <button type="button" onClick={() => setReloadKey(value => value + 1)} className="text-blue-400 hover:underline">Retry</button>
                </div>
            )}
            {unavailableLabels.size > 0 && (
                <button type="button" onClick={() => setUnavailableLabels(new Set())}
                    className="text-xs text-blue-400 hover:underline">Retry unavailable channels</button>
            )}
            <div className={`grid ${gridCols} gap-2 md:gap-3`}>
                {displayFeeds.map((feed) => (
                    <FeedCard
                        key={`${feed.label}:${feed.urls[0]}`}
                        feed={feed}
                        onEnlarge={(url, label) => setExpandedVideo({ url, label })}
                        onUnavailable={resolvedFeeds?.some(source => source.label === feed.label) ? markUnavailable : undefined}
                    />
                ))}
            </div>

            {/* No results state */}
            {displayFeeds.length === 0 && directoryStatus === 'ready' && (
                <div className="text-center py-12 text-slate-600">
                    <p className="text-sm font-bold uppercase tracking-widest">{searchTerm ? `No feeds match "${searchTerm}"` : lockedCountry ? `No ${lockedCountry} channels are live right now` : 'No channels are live right now'}</p>
                    <p className="text-xs mt-1">Search again later.</p>
                </div>
            )}

            {/* Fullscreen Modal */}
            {expandedVideo && createPortal(
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${expandedVideo.label} fullscreen video`}
                    className="fixed inset-0 z-[2147483647] flex h-[100dvh] w-screen items-center justify-center overflow-hidden overscroll-none bg-black animate-in fade-in duration-200"
                >
                    <div className="relative h-full w-full overflow-hidden bg-black">
                        <iframe
                            src={expandedVideo.url}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                            className="w-full h-full"
                            title={`${expandedVideo.label} — Fullscreen`}
                        />
                        {/* Label badge */}
                        <div className="absolute top-4 left-4 flex items-center gap-3">
                            <div className="bg-slate-900/90 backdrop-blur-md px-4 py-2 rounded-full border border-slate-700 flex items-center gap-2">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                                </span>
                                <span className="text-white text-xs font-bold uppercase tracking-widest">{expandedVideo.label}</span>
                            </div>
                        </div>
                        {/* Close */}
                        <button
                            onClick={() => setExpandedVideo(null)}
                            aria-label="Close fullscreen video"
                            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-2.5 rounded-full backdrop-blur-md transition-colors border border-white/20 group"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-5 h-5 group-hover:scale-110 transition-transform">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};
